/** What to know before you start talking.
 *
 * The client sent a screenshot of a competitor's pre-session screen and asked
 * for the same: instructions BEFORE the conversation starts, not discovered
 * halfway through it. Two different things belong there, and they are kept
 * visually distinct because they answer different questions:
 *
 *   "Prepare first"  — how the real exam works. In telc/Goethe you get
 *                      20–30 minutes with the task before you speak, and a
 *                      learner who does not know that thinks the exam is
 *                      far harder than it is.
 *   "How this works" — how OUR microphone behaves. Every line here is a
 *                      support question we would otherwise get: it stopped
 *                      listening, it talked over me, nothing happened.
 *
 * `mode` picks which prep note applies — there is no preparation time in a
 * free chat, and pretending otherwise would be inventing an exam rule.
 */
import { useState } from "react";
import { useT } from "../../i18n";

export function SpeakingBriefing({ mode = "exam" }: { mode?: "exam" | "free" }) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const tips = ["speak", "done", "turns", "pause", "trouble"] as const;

  return (
    <div className="space-y-3">
      {mode === "exam" && (
        <div className="rounded-control border border-warning/40 bg-warning/10 p-3">
          <p className="text-sm font-bold text-ink">{t("briefing.prepTitle")}</p>
          <p className="mt-1 text-sm text-muted">{t("briefing.prepBody")}</p>
        </div>
      )}

      <div className="rounded-control border border-line bg-canvas">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full flex-wrap items-center justify-between gap-2 p-3 text-left"
        >
          <span className="text-sm font-bold text-ink">{t("briefing.title")}</span>
          <span aria-hidden className="text-muted">{open ? "−" : "+"}</span>
        </button>
        {open && (
          <ul className="space-y-2 px-3 pb-3">
            {tips.map((tip) => (
              <li key={tip}>
                <p className="text-sm font-semibold text-ink">
                  {t(`briefing.${tip}.title`)}
                </p>
                <p className="text-sm text-muted">{t(`briefing.${tip}.body`)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
